// AP-045 v3 · Premium Landing Page inspirada em HubSpot/Salesforce/Stripe/Monday/Notion
// com identidade JMS. Rota "/" pública. Header fixo, hero Kreyòl, dashboard mockup,
// stats animados, features, IA demo, portal, JMS TECH, módulos, how-it-works, planos,
// gallery, video, testimonials, FAQ, CTA, footer, botão "Explorar Demonstração".
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ShieldCheck, UserRound, ArrowRight, PlayCircle, MessageCircle, Sparkles,
  Bot, Users, FileText, CreditCard, Wallet, Calendar, MessageSquareText, BarChart3,
  GraduationCap, Settings2, Plug, Headphones, Instagram, Facebook, Youtube, Linkedin,
  CheckCircle2, ChevronDown, Zap, TrendingUp, Award, Rocket, Send, Building2, Globe,
  Star, Menu, X, Play, Eye, LogIn,
} from "lucide-react";

const WHATSAPP_URL = "https://wa.me/554984183430?text=" + encodeURIComponent(
  "Bonjou JMS MULTI SERVICE LTDA.\nMwen bezwen asistans ak JMS ERP.\nMèsi."
);
const VIDEO_URL = "https://www.youtube.com/watch?v=MD9T5tOd3KA&t=18s";

// ============================================================================
// Utilities
// ============================================================================
function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setSeen(true), { threshold });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, seen];
}

function Counter({ to, suffix = "", duration = 1600 }) {
  const [ref, seen] = useInView(0.4);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!seen) return;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setN(Math.floor(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [seen, to, duration]);
  return <span ref={ref}>{n.toLocaleString("pt-BR")}{suffix}</span>;
}

function FadeIn({ children, delay = 0, className = "" }) {
  const [ref, seen] = useInView(0.15);
  return (
    <div ref={ref} className={className}
      style={{
        opacity: seen ? 1 : 0,
        transform: seen ? "translateY(0)" : "translateY(20px)",
        transition: `opacity 700ms ease ${delay}ms, transform 700ms ease ${delay}ms`,
      }}>{children}</div>
  );
}

// ============================================================================
// Header (fixed, translucent)
// ============================================================================
function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", on); on();
    return () => window.removeEventListener("scroll", on);
  }, []);
  const menu = [
    ["Recursos", "#features"], ["Soluções", "#solutions"], ["Módulos", "#modules"],
    ["Preços", "#pricing"], ["Empresas", "#companies"], ["Sobre", "#about"], ["Contato", "#contact"],
  ];
  return (
    <header data-testid="landing-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200 shadow-sm" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
          <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-primary to-blue-600 grid place-items-center shadow-lg shadow-primary/30">
            <ShieldCheck className="h-4 w-4 text-white" />
          </div>
          <span className="font-black text-lg tracking-tight">JMS ERP</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-1">
          {menu.map(([l, h]) => (
            <a key={h} href={h} className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-primary transition rounded-lg hover:bg-slate-100" data-testid={`nav-${l.toLowerCase()}`}>{l}</a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-2">
          <Link to="/login"><Button variant="ghost" size="sm" data-testid="header-login"><LogIn className="h-3.5 w-3.5 mr-1" /> Entrar</Button></Link>
          <Link to="/login"><Button variant="outline" size="sm" data-testid="header-create">Criar Conta</Button></Link>
          <a href="#pricing"><Button size="sm" className="bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-700" data-testid="header-trial">Teste Grátis</Button></a>
        </div>
        <Button variant="ghost" size="sm" className="lg:hidden" onClick={() => setOpen((v) => !v)}>{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</Button>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 px-4 py-3 space-y-1">
          {menu.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2 text-sm">{l}</a>)}
          <div className="pt-2 flex gap-2"><Link to="/login" className="flex-1"><Button variant="outline" size="sm" className="w-full">Entrar</Button></Link><a href="#pricing" className="flex-1"><Button size="sm" className="w-full">Teste Grátis</Button></a></div>
        </div>
      )}
    </header>
  );
}

// ============================================================================
// Hero + Dashboard Mockup
// ============================================================================
function Hero() {
  return (
    <section className="relative pt-32 pb-20 overflow-hidden" data-testid="landing-hero">
      <div aria-hidden className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50" />
        <div className="absolute top-10 right-10 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-10 left-10 h-96 w-96 rounded-full bg-indigo-200/40 blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center">
        <FadeIn>
          <Badge variant="outline" className="mb-4 bg-white/70 backdrop-blur border-primary/30 text-primary">
            <Sparkles className="h-3 w-3 mr-1" /> Novo · Assistente IA em Kreyòl e Português
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-4">
            Yon sèl sistèm pou<br />
            <span className="bg-gradient-to-r from-primary via-blue-600 to-indigo-600 bg-clip-text text-transparent">jere tout biznis ou.</span>
          </h1>
          <p className="text-lg text-slate-600 mb-3 leading-relaxed">
            Dijitalize antrepriz ou avèk yon platfòm entelijan ki mete tout sèvis ou yo nan yon sèl kote.
          </p>
          <p className="text-sm text-slate-500 mb-6 flex flex-wrap gap-x-2 gap-y-1">
            {["CRM", "Clientes", "Processos", "Pagamentos", "Financeiro", "RH", "Portal Cliente", "IA", "JMS TECH", "Analytics"].map((k) => (
              <span key={k} className="inline-flex items-center"><CheckCircle2 className="h-3 w-3 text-emerald-500 mr-1" />{k}</span>
            ))}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/login"><Button size="lg" className="bg-gradient-to-r from-primary to-blue-600 shadow-xl shadow-primary/30 hover:scale-105 transition" data-testid="hero-start"><Rocket className="h-4 w-4 mr-2" />Começar Agora</Button></Link>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"><Button size="lg" variant="outline" data-testid="hero-demo"><Send className="h-4 w-4 mr-2" />Solicitar Demonstração</Button></a>
            <a href={VIDEO_URL} target="_blank" rel="noopener noreferrer"><Button size="lg" variant="ghost" data-testid="hero-video"><PlayCircle className="h-4 w-4 mr-2" />Assistir Vídeo</Button></a>
          </div>
          <div className="mt-8 flex items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> LGPD Compliant</span>
            <span className="flex items-center gap-1"><Zap className="h-3 w-3" /> 99,9% uptime</span>
            <span className="flex items-center gap-1"><Star className="h-3 w-3 text-amber-400 fill-amber-400" /> 4.9/5</span>
          </div>
        </FadeIn>
        <FadeIn delay={150}>
          <DashboardMockup />
        </FadeIn>
      </div>
    </section>
  );
}

function DashboardMockup() {
  const events = [
    { icon: UserRound,      color: "bg-emerald-500", title: "Cliente novo",       sub: "Marie Joseph criou conta", t: "agora" },
    { icon: CreditCard,     color: "bg-primary",     title: "Pagamento recebido", sub: "R$ 1.200,00 · via PIX",    t: "1min" },
    { icon: Bot,            color: "bg-indigo-500",  title: "IA respondeu",       sub: "Escalação → ticket #482",  t: "2min" },
    { icon: FileText,       color: "bg-amber-500",   title: "Processo atualizado",sub: "PROC-2026-000114 → Análise", t: "5min" },
  ];
  return (
    <div className="relative">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-300/40 p-4 space-y-3" data-testid="hero-mockup">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><div className="h-2 w-2 rounded-full bg-rose-400" /><div className="h-2 w-2 rounded-full bg-amber-400" /><div className="h-2 w-2 rounded-full bg-emerald-400" /></div>
          <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">JMS ERP · Dashboard</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[["Clientes", "1.847", TrendingUp, "text-emerald-600"], ["Receita/mês", "R$ 84k", Wallet, "text-primary"], ["Conversão", "68%", BarChart3, "text-indigo-600"]].map(([l, v, Ic, c]) => (
            <div key={l} className="rounded-xl bg-slate-50 p-3">
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-bold"><Ic className={`h-3 w-3 ${c}`} /> {l}</div>
              <p className="text-lg font-black tabular-nums mt-1">{v}</p>
            </div>
          ))}
        </div>
        <div className="rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 p-3 h-24 flex items-end gap-1">
          {[40, 55, 30, 70, 45, 80, 65, 90, 75, 100, 85, 95].map((h, i) => (
            <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-primary to-blue-400" style={{ height: `${h}%`, opacity: 0.5 + i / 24 }} />
          ))}
        </div>
        <div className="space-y-2">
          {events.map((e, i) => (
            <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition"
                  style={{ animation: `slideIn 500ms ease ${i * 200}ms both` }}>
              <div className={`h-8 w-8 rounded-lg ${e.color} text-white grid place-items-center flex-shrink-0`}><e.icon className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold truncate">{e.title}</p>
                <p className="text-[10px] text-slate-500 truncate">{e.sub}</p>
              </div>
              <span className="text-[10px] text-slate-400">{e.t}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute -top-6 -right-6 rounded-xl bg-white shadow-xl border border-slate-200 px-3 py-2 flex items-center gap-2 hidden md:flex" style={{ animation: "float 3s ease-in-out infinite" }}>
        <div className="h-8 w-8 rounded-lg bg-indigo-100 grid place-items-center"><Bot className="h-4 w-4 text-indigo-600" /></div>
        <div><p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Notificação</p><p className="text-xs font-bold">IA respondeu 12 clientes</p></div>
      </div>
      <div className="absolute -bottom-6 -left-6 rounded-xl bg-white shadow-xl border border-slate-200 px-3 py-2 flex items-center gap-2 hidden md:flex" style={{ animation: "float 3s ease-in-out infinite 1.5s" }}>
        <div className="h-8 w-8 rounded-lg bg-emerald-100 grid place-items-center"><Calendar className="h-4 w-4 text-emerald-600" /></div>
        <div><p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Agenda</p><p className="text-xs font-bold">3 reuniões hoje</p></div>
      </div>
      <style>{`
        @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes slideIn { from { opacity:0; transform:translateX(-8px); } to { opacity:1; transform:translateX(0); } }
      `}</style>
    </div>
  );
}

// ============================================================================
// Companies logos strip
// ============================================================================
function Companies() {
  const logos = ["JMS Multi Service", "Ecclesia ERP", "JMS TECH", "JMS Sabor", "Gerson Advocacia", "AFD Consultoria", "Lumière Partners", "Haïti Import"];
  return (
    <section id="companies" className="py-12 border-y border-slate-100" data-testid="landing-companies">
      <div className="max-w-7xl mx-auto px-4">
        <p className="text-center text-xs uppercase tracking-widest text-slate-500 font-bold mb-6">Empresas que confiam no JMS ERP</p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6 items-center">
          {logos.map((l) => (
            <div key={l} className="text-center text-sm font-black tracking-tight text-slate-400 hover:text-slate-700 transition truncate">{l}</div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Stats
// ============================================================================
function Stats() {
  const items = [
    ["clientes",      2000, "+"],
    ["processos",     1000, "+"],
    ["disponibilidade", 99.9, "%"],
    ["suporte",         24, "/7"],
    ["empresas",        500, "+"],
  ];
  return (
    <section className="py-16 bg-gradient-to-br from-slate-900 to-blue-900 text-white" data-testid="landing-stats">
      <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-5 gap-8">
        {items.map(([l, n, s], i) => (
          <FadeIn key={l} delay={i * 100}>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-black tabular-nums bg-gradient-to-br from-white to-blue-200 bg-clip-text text-transparent">
                <Counter to={n} suffix={s} />
              </p>
              <p className="text-xs uppercase tracking-widest text-blue-200 font-bold mt-1">{l}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

// ============================================================================
// Why choose (feature grid)
// ============================================================================
function Features() {
  const feats = [
    [Users, "CRM Completo", "Centralize contatos, pipeline, follow-up e histórico de cada cliente."],
    [UserRound, "Portal do Cliente", "Auto-atendimento com documentos, pagamentos e agenda."],
    [Bot, "Assistente IA", "Responde em Kreyòl/PT/EN, consulta ERP e escala tickets."],
    [Wallet, "Gestão Financeira", "Contas a receber/pagar, conciliação e relatórios em tempo real."],
    [FileText, "Documentos", "Upload, assinatura, categorização e busca inteligente."],
    [Building2, "Processos", "Kanban dos serviços com etapas, responsáveis e SLA."],
    [Calendar, "Agenda", "Compromissos com clientes, staff e sala compartilhada."],
    [Award, "RH & Folha", "Colaboradores, holerite, férias e portal do funcionário."],
    [MessageSquareText, "Comunicação", "Chat interno com canais por equipe e menções."],
    [BarChart3, "Analytics", "KPIs prontos e drill-down para todas as áreas."],
    [GraduationCap, "JMS TECH", "LMS integrado — cursos, certificados, coaching."],
  ];
  return (
    <section id="features" className="py-20" data-testid="landing-features">
      <div className="max-w-7xl mx-auto px-4">
        <FadeIn className="text-center max-w-2xl mx-auto mb-12">
          <Badge variant="outline" className="mb-3 border-primary/30 text-primary">Por que escolher</Badge>
          <h2 className="text-3xl md:text-4xl font-black mb-3">Um ecossistema completo, não um punhado de ferramentas.</h2>
          <p className="text-slate-600">Substitua 10 softwares diferentes por uma única plataforma integrada.</p>
        </FadeIn>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {feats.map(([Ic, t, d], i) => (
            <FadeIn key={t} delay={i * 60}>
              <div className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-primary/50 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10 transition-all cursor-default">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-primary/10 to-indigo-100 grid place-items-center mb-3 group-hover:from-primary group-hover:to-blue-600 transition">
                  <Ic className="h-5 w-5 text-primary group-hover:text-white transition" />
                </div>
                <p className="font-bold mb-1">{t}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{d}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// IA section
// ============================================================================
function AiSection() {
  return (
    <section id="solutions" className="py-20 bg-gradient-to-br from-indigo-50 via-white to-blue-50 relative overflow-hidden">
      <div aria-hidden className="absolute top-20 right-20 h-64 w-64 rounded-full bg-primary/10 blur-3xl -z-0" />
      <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center relative">
        <FadeIn>
          <Badge className="mb-3 bg-indigo-100 text-indigo-700 border-indigo-200"><Bot className="h-3 w-3 mr-1" /> Assistente IA</Badge>
          <h2 className="text-3xl md:text-4xl font-black mb-3">Sua IA fala Kreyòl, PT, FR, EN — e realmente consulta seus dados.</h2>
          <p className="text-slate-600 mb-6 leading-relaxed">
            O Assistente IA JMS conversa com seus clientes 24/7, responde sobre processos, pagamentos, documentos e agenda usando dados reais do ERP.
            Quando não sabe, escala automaticamente para sua equipe humana.
          </p>
          <ul className="space-y-2 mb-6">
            {["Streaming em tempo real (SSE)", "GPT-5.4 + Claude Sonnet 4.6 (fallback)", "Consulta ERP direto — nunca inventa", "Escalação para tickets humanos", "Base de conhecimento personalizável"].map((it) => (
              <li key={it} className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" /> {it}</li>
            ))}
          </ul>
          <Link to="/login"><Button size="lg" className="bg-gradient-to-r from-indigo-600 to-primary shadow-xl shadow-indigo-500/20" data-testid="ai-try"><Bot className="h-4 w-4 mr-2" />Experimentar IA</Button></Link>
        </FadeIn>
        <FadeIn delay={200}>
          <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-indigo-500/10 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-primary text-white p-3 flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-white/20 grid place-items-center"><Bot className="h-4 w-4" /></div>
              <div><p className="text-sm font-bold">Assistente IA JMS</p><p className="text-[10px] opacity-80">Online · Kreyòl · PT · FR · EN</p></div>
            </div>
            <div className="p-4 space-y-3 bg-slate-50 min-h-[280px]">
              <div className="flex justify-end"><div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-primary text-white p-3 text-sm">Onde está meu processo?</div></div>
              <div className="flex"><div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white border border-slate-200 p-3 text-sm">
                Mwen verifye enfòmasyon ki nan dosye ou. Seu processo <strong>PROC-2026-000114</strong> está em <span className="text-emerald-600 font-bold">Análise</span>.<br />
                <span className="block mt-2 text-xs text-slate-500 font-bold">Documentos pendentes:</span>
                <ul className="text-xs mt-1 space-y-0.5">
                  <li>• Passaporte</li><li>• Certidão</li><li>• Foto 3x4</li>
                </ul>
              </div></div>
              <div className="flex"><div className="rounded-2xl rounded-tl-sm bg-white border border-slate-200 p-3 text-sm text-slate-400 italic flex items-center gap-1">
                <span className="flex gap-0.5"><span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" /><span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} /><span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} /></span>
              </div></div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ============================================================================
// Portal + JMS TECH showcase (2-column)
// ============================================================================
function Showcase({ id, badge, title, subtitle, items, side = "left", accent = "primary" }) {
  const media = (
    <div className={`rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white shadow-2xl shadow-slate-300/40 p-4 space-y-2`}>
      {items.map(([Ic, l, sub], i) => (
        <div key={l} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-100 hover:shadow-md transition"
              style={{ animation: `slideIn 500ms ease ${i * 80}ms both` }}>
          <div className={`h-9 w-9 rounded-lg bg-${accent}/10 grid place-items-center flex-shrink-0`}><Ic className={`h-4 w-4 text-${accent}`} /></div>
          <div><p className="text-sm font-bold">{l}</p><p className="text-xs text-slate-500">{sub}</p></div>
        </div>
      ))}
    </div>
  );
  const text = (
    <div>
      <Badge variant="outline" className="mb-3">{badge}</Badge>
      <h2 className="text-3xl md:text-4xl font-black mb-3">{title}</h2>
      <p className="text-slate-600 mb-6 leading-relaxed">{subtitle}</p>
      <ul className="space-y-2">
        {items.map(([Ic, l]) => <li key={l} className="flex items-center gap-2 text-sm"><Ic className="h-4 w-4 text-primary" /> {l}</li>)}
      </ul>
    </div>
  );
  return (
    <section id={id} className="py-20" data-testid={`landing-${id}`}>
      <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center">
        <FadeIn>{side === "left" ? text : media}</FadeIn>
        <FadeIn delay={150}>{side === "left" ? media : text}</FadeIn>
      </div>
    </section>
  );
}

// ============================================================================
// Modules grid
// ============================================================================
function Modules() {
  const mods = [
    [Users, "Clientes"], [Building2, "Processos"], [CreditCard, "Pagamentos"], [Wallet, "Financeiro"],
    [Award, "RH"], [Calendar, "Agenda"], [FileText, "Documentos"], [UserRound, "Portal"],
    [MessageSquareText, "Comunicação"], [BarChart3, "Analytics"], [Bot, "IA"], [GraduationCap, "JMS TECH"],
    [Settings2, "Configurações"], [Plug, "Integrações"],
  ];
  return (
    <section id="modules" className="py-20 bg-slate-50" data-testid="landing-modules">
      <div className="max-w-7xl mx-auto px-4">
        <FadeIn className="text-center max-w-2xl mx-auto mb-12">
          <Badge variant="outline" className="mb-3">14 módulos</Badge>
          <h2 className="text-3xl md:text-4xl font-black mb-3">Um módulo para cada necessidade da sua empresa.</h2>
          <p className="text-slate-600">Ative apenas o que você precisa. Cresça em qualquer direção.</p>
        </FadeIn>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {mods.map(([Ic, l], i) => (
            <FadeIn key={l} delay={i * 40}>
              <div className="rounded-xl bg-white border border-slate-200 p-4 text-center hover:border-primary hover:-translate-y-1 hover:shadow-lg transition-all cursor-default">
                <Ic className="h-6 w-6 text-primary mx-auto mb-2" />
                <p className="text-xs font-bold">{l}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// How it works
// ============================================================================
function HowItWorks() {
  const steps = [
    ["Criar Conta", "Cadastre-se em 30 segundos.", Rocket],
    ["Cadastrar Empresa", "Configure sua marca, CNPJ e equipe.", Building2],
    ["Adicionar Equipe", "Convide colaboradores por e-mail.", Users],
    ["Cadastrar Clientes", "Importe planilha ou cadastre manual.", UserRound],
    ["Começar a Trabalhar", "Tudo pronto — use hoje mesmo.", Zap],
  ];
  return (
    <section className="py-20" data-testid="landing-how">
      <div className="max-w-7xl mx-auto px-4">
        <FadeIn className="text-center mb-12"><Badge variant="outline" className="mb-3">Como funciona</Badge><h2 className="text-3xl md:text-4xl font-black">5 passos para transformar sua operação.</h2></FadeIn>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map(([t, d, Ic], i) => (
            <FadeIn key={t} delay={i * 100}>
              <div className="text-center">
                <div className="relative mx-auto mb-3 h-14 w-14 rounded-2xl bg-gradient-to-br from-primary to-blue-600 grid place-items-center text-white shadow-xl shadow-primary/30">
                  <Ic className="h-6 w-6" />
                  <span className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white text-primary text-xs font-black grid place-items-center border-2 border-primary shadow">{i + 1}</span>
                </div>
                <p className="font-bold text-sm">{t}</p>
                <p className="text-xs text-slate-500 mt-1">{d}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Pricing — AP-141 · 100% dinâmico. Consome `/api/public/plans` que retorna
// somente planos publicados (status active/promotion, portal_visible=true,
// visibility public/partners). Zero hardcode.
// ============================================================================
const BILLING_LABELS = {
  one_time: "pagamento único",
  monthly: "/mês", quarterly: "/trimestre", semester: "/semestre",
  yearly: "/ano", lifetime: "vitalício",
};
const CURRENCY_SYMBOLS = { USD: "US$", BRL: "R$", EUR: "€", HTG: "G" };

function formatPrice(cur, val) {
  if (val == null || val === "" || Number(val) <= 0) return null;
  const sym = CURRENCY_SYMBOLS[cur] || cur;
  const n = Number(val);
  return `${sym} ${n.toLocaleString("pt-BR", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
}

function Pricing() {
  const [state, setState] = useState({ loading: true, items: [], error: null });
  useEffect(() => {
    const api = process.env.REACT_APP_BACKEND_URL;
    fetch(`${api}/api/public/plans`)
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((d) => setState({ loading: false, items: d.items || [], error: null }))
      .catch((e) => setState({ loading: false, items: [], error: String(e) }));
  }, []);

  const { loading, items, error } = state;

  return (
    <section id="pricing" className="py-20 bg-slate-50" data-testid="landing-pricing">
      <div className="max-w-7xl mx-auto px-4">
        <FadeIn className="text-center mb-12">
          <Badge variant="outline" className="mb-3">Planos</Badge>
          <h2 className="text-3xl md:text-4xl font-black mb-3">Preço honesto. Sem letras miúdas.</h2>
          <p className="text-slate-600">Comece com 14 dias grátis · cancele quando quiser · sem taxa de setup.</p>
        </FadeIn>

        {loading && (
          <div className="text-center py-12 text-slate-400 text-sm" data-testid="pricing-loading">Carregando planos...</div>
        )}

        {!loading && items.length === 0 && (
          <div className="text-center py-16 rounded-2xl border border-dashed border-slate-300 bg-white max-w-xl mx-auto" data-testid="pricing-empty">
            <Sparkles className="h-8 w-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-600">Nenhum plano publicado no momento.</p>
            <p className="text-xs text-slate-400 mt-1">Fale conosco para uma proposta personalizada.</p>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block">
              <Button size="sm" variant="outline"><Send className="h-3.5 w-3.5 mr-1" /> Falar com vendas</Button>
            </a>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className={`grid gap-5 max-w-5xl mx-auto ${items.length === 1 ? "md:grid-cols-1 max-w-md" : items.length === 2 ? "md:grid-cols-2 max-w-3xl" : "md:grid-cols-3"}`}>
            {items.map((p, i) => {
              const priceFull = formatPrice(p.currency, p.price_usd);
              const pricePromo = formatPrice(p.currency, p.promo_price_usd);
              const display = pricePromo || priceFull;
              const cycle = BILLING_LABELS[p.billing_cycle] || "";
              return (
                <FadeIn key={p.id || p.code} delay={i * 100}>
                  <div className={`relative rounded-2xl border-2 p-6 bg-white transition-all hover:-translate-y-2 hover:shadow-2xl h-full flex flex-col ${p.featured ? "border-primary shadow-xl shadow-primary/20" : "border-slate-200"}`}
                        data-testid={`pricing-plan-${p.code}`}>
                    {p.featured && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary to-blue-600 text-white">Mais popular</Badge>}
                    {p.status === "promotion" && <Badge className="absolute top-3 right-3 bg-pink-100 text-pink-700 border-pink-200">Promoção</Badge>}

                    <p className="text-sm font-bold text-slate-500 uppercase tracking-widest" data-testid={`pricing-name-${p.code}`}>{p.name}</p>
                    {p.category && <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">{p.category}</p>}

                    <div className="mt-3 flex items-baseline gap-1" data-testid={`pricing-price-${p.code}`}>
                      {display ? (
                        <>
                          {pricePromo && priceFull && <span className="text-sm text-slate-400 line-through mr-1">{priceFull}</span>}
                          <span className="text-4xl font-black">{display}</span>
                          {cycle && <span className="text-sm text-slate-500">{cycle}</span>}
                        </>
                      ) : (
                        <span className="text-2xl font-black text-slate-600">Consulte-nos</span>
                      )}
                    </div>

                    {p.description && <p className="text-sm text-slate-500 mt-2">{p.description}</p>}

                    {(p.features && p.features.length > 0) && (
                      <ul className="mt-6 space-y-2" data-testid={`pricing-features-${p.code}`}>
                        {p.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" /> <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {p.limits?.max_students && (
                      <p className="mt-3 text-xs text-slate-500 flex items-center gap-1">
                        <Users className="h-3 w-3" /> Até {p.limits.max_students} alunos
                      </p>
                    )}

                    {p.modules && p.modules.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1" data-testid={`pricing-modules-${p.code}`}>
                        {p.modules.map((m) => (
                          <span key={m} className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{m}</span>
                        ))}
                      </div>
                    )}

                    <div className="mt-auto pt-6">
                      <Link to={display ? "/login" : "#"} onClick={display ? undefined : (e) => { e.preventDefault(); window.open(WHATSAPP_URL, "_blank"); }} className="block">
                        <Button className={`w-full ${p.featured ? "bg-gradient-to-r from-primary to-blue-600" : ""}`} variant={p.featured ? "default" : "outline"} data-testid={`pricing-cta-${p.code}`}>
                          {display ? "Começar grátis" : "Falar com vendas"}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        )}

        {error && (
          <p className="text-center text-xs text-rose-500 mt-6" data-testid="pricing-error">
            Não foi possível carregar os planos no momento. Recarregue a página ou entre em contato.
          </p>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// Testimonials
// ============================================================================
function Testimonials() {
  const t = [
    { n: "Marie Joseph",   c: "JMS Multi Service", q: "Antes usávamos 6 sistemas. Hoje é só o JMS ERP. Reduzi 40% do tempo administrativo." },
    { n: "Carlos Andrade", c: "AFD Consultoria",   q: "O Assistente IA em Kreyòl mudou a forma como atendemos. Clientes ficam impressionados." },
    { n: "Renata Costa",   c: "Lumière Partners",  q: "Portal do Cliente + JMS TECH nos deu um diferencial que nenhum concorrente tem." },
  ];
  return (
    <section className="py-20" data-testid="landing-testimonials">
      <div className="max-w-7xl mx-auto px-4">
        <FadeIn className="text-center mb-12"><Badge variant="outline" className="mb-3">Depoimentos</Badge><h2 className="text-3xl md:text-4xl font-black">Empresas reais, resultados reais.</h2></FadeIn>
        <div className="grid md:grid-cols-3 gap-5">
          {t.map((x, i) => (
            <FadeIn key={x.n} delay={i * 120}>
              <div className="rounded-2xl bg-gradient-to-br from-white to-slate-50 border border-slate-200 p-6 shadow-lg h-full flex flex-col">
                <div className="flex gap-0.5 mb-3">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className="h-4 w-4 fill-amber-400 text-amber-400" />)}</div>
                <p className="text-slate-700 italic flex-1">"{x.q}"</p>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-sm font-bold">{x.n}</p><p className="text-xs text-slate-500">{x.c}</p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// FAQ
// ============================================================================
function Faq() {
  const items = [
    ["Preciso instalar algo?", "Não. JMS ERP é 100% na nuvem — acesse do navegador em qualquer dispositivo."],
    ["Meus dados ficam seguros?", "Sim. Criptografia em trânsito e repouso, backups diários e conformidade LGPD."],
    ["Posso migrar do meu sistema atual?", "Sim. Importamos planilhas, CSVs e temos integração com os principais CRMs."],
    ["Quanto tempo leva a implantação?", "Starter: mesmo dia. Professional: 3-5 dias com onboarding guiado. Enterprise: 2 semanas."],
    ["A IA funciona em Kreyòl?", "Sim, com prompt nativo em Kreyòl e fallback para PT, FR e EN quando o cliente escreve nesses idiomas."],
    ["Consigo personalizar para o meu negócio?", "Sim. Módulos ativáveis, permissões por papel (RBAC), campos customizados e webhooks."],
    ["Tem versão mobile?", "Sim. Interface responsiva otimizada para tablet e celular; PWA em breve."],
    ["Como funciona o teste grátis?", "14 dias, todos os recursos, sem cartão de crédito. Ao final, escolha um plano ou cancele."],
    ["Posso cancelar quando quiser?", "Sim. Sem multa e sem pergunta constrangedora — cancele em 1 clique."],
    ["Vocês oferecem treinamento?", "Sim. Onboarding em vídeo, webinars semanais e coaching 1:1 no Enterprise."],
    ["Como funciona o Portal do Cliente?", "Cada cliente recebe login próprio para ver processos, pagar, enviar documentos e falar com a IA."],
    ["JMS TECH é o mesmo sistema?", "Sim. É o módulo LMS integrado — cursos, certificados, coaching e biblioteca."],
    ["Tem integração com meu banco?", "Sim. PIX, boleto, cartão via Stripe/Mercado Pago; conciliação automática."],
    ["Vocês têm API?", "Sim, no plano Enterprise. REST + webhooks para eventos em tempo real."],
    ["Qual o SLA?", "99,9% no Professional; 99,95% com suporte 24/7 no Enterprise."],
  ];
  const [open, setOpen] = useState(0);
  return (
    <section id="about" className="py-20 bg-slate-50" data-testid="landing-faq">
      <div className="max-w-3xl mx-auto px-4">
        <FadeIn className="text-center mb-10"><Badge variant="outline" className="mb-3">Perguntas frequentes</Badge><h2 className="text-3xl md:text-4xl font-black">Tudo o que você precisa saber.</h2></FadeIn>
        <div className="space-y-2">
          {items.map(([q, a], i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 overflow-hidden">
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition" data-testid={`faq-q-${i}`}>
                <span className="font-bold text-sm">{q}</span>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <div className="px-4 pb-4 text-sm text-slate-600 border-t border-slate-100 pt-3">{a}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Final CTA + Footer
// ============================================================================
function CTA() {
  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-primary via-blue-600 to-indigo-700 text-white relative overflow-hidden" data-testid="landing-cta">
      <div aria-hidden className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,.15),transparent_50%)]" />
      <div className="max-w-4xl mx-auto px-4 text-center relative">
        <FadeIn>
          <h2 className="text-3xl md:text-5xl font-black mb-4 leading-tight">Pronto para transformar sua empresa?</h2>
          <p className="text-blue-100 text-lg mb-8">Junte-se a mais de 500 empresas que já operam com o JMS ERP.</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/login"><Button size="lg" className="bg-white text-primary hover:bg-slate-100 shadow-2xl" data-testid="cta-create"><Rocket className="h-4 w-4 mr-2" /> Criar Conta</Button></Link>
            <a href="#pricing"><Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-testid="cta-trial">Teste Grátis 14 dias</Button></a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"><Button size="lg" variant="ghost" className="text-white hover:bg-white/10" data-testid="cta-schedule"><Send className="h-4 w-4 mr-2" />Agendar Demonstração</Button></a>
          </div>
          <Link to="/demo" className="mt-6 inline-flex items-center gap-2 text-sm text-blue-100 hover:text-white transition" data-testid="cta-explore-demo">
            <Eye className="h-4 w-4" /> Explorar Demonstração (sem cadastro) <ArrowRight className="h-3 w-3" />
          </Link>
        </FadeIn>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-slate-950 text-slate-300 py-12" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-primary to-blue-600 grid place-items-center"><ShieldCheck className="h-4 w-4 text-white" /></div>
            <span className="font-black text-lg text-white">JMS ERP</span>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">Plataforma ERP multi-tenant · Multi-idioma · IA · Portal do Cliente · JMS TECH · Analytics.</p>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-white mb-3">Produto</p>
          <ul className="space-y-1 text-xs">
            <li><a href="#features" className="hover:text-white">Recursos</a></li>
            <li><a href="#modules" className="hover:text-white">Módulos</a></li>
            <li><a href="#pricing" className="hover:text-white">Preços</a></li>
            <li><Link to="/demo" className="hover:text-white">Demonstração</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-white mb-3">Legal</p>
          <ul className="space-y-1 text-xs">
            <li><a href="/privacidade" className="hover:text-white">Privacidade</a></li>
            <li><a href="/lgpd" className="hover:text-white">LGPD</a></li>
            <li><a href="/termos" className="hover:text-white">Termos</a></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-white mb-3">Contato</p>
          <ul className="space-y-2 text-xs">
            <li><a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a></li>
            <li><a href="mailto:contato@jmsmultiservice.com.br" className="flex items-center gap-2 hover:text-white"><Send className="h-3.5 w-3.5" /> contato@jmsmultiservice.com.br</a></li>
          </ul>
          <div className="flex items-center gap-3 mt-4">
            <a href="#" className="text-slate-500 hover:text-white"><Instagram className="h-4 w-4" /></a>
            <a href="#" className="text-slate-500 hover:text-white"><Facebook className="h-4 w-4" /></a>
            <a href="#" className="text-slate-500 hover:text-white"><Youtube className="h-4 w-4" /></a>
            <a href="#" className="text-slate-500 hover:text-white"><Linkedin className="h-4 w-4" /></a>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 pt-8 mt-8 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3 text-[10px] text-slate-500">
        <span>© 2026 JMS MULTI SERVICE LTDA · Todos os direitos reservados</span>
        <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> pt-BR · Kreyòl · FR · EN</span>
      </div>
    </footer>
  );
}

// ============================================================================
// Root
// ============================================================================
export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      <Header />
      <main>
        <Hero />
        <Companies />
        <Stats />
        <Features />
        <AiSection />
        <Showcase id="portal" badge="Portal do Cliente" side="left"
          title="Seu cliente com autonomia total."
          subtitle="Auto-atendimento premium: seu cliente vê processos, paga, envia documentos, agenda e conversa com a IA — sem ligar para você."
          items={[[UserRound, "Login com nome + WhatsApp", "Sem senha complexa"], [FileText, "Documentos digitais", "Upload direto pelo cliente"], [CreditCard, "Pagamentos online", "PIX · Cartão · Boleto"], [Calendar, "Agenda self-service", "Cliente marca reunião sozinho"], [Bot, "IA no portal", "Responde 24/7 em Kreyòl"]]}
        />
        <Showcase id="tech" badge="JMS TECH · LMS" side="right"
          title="Um LMS profissional embutido no seu ERP."
          subtitle="Venda cursos, emita certificados, ofereça coaching e mentorias. Tudo integrado com clientes e financeiro."
          items={[[GraduationCap, "Cursos + módulos + aulas", "Vídeo, PDF, quiz, exercício"], [Award, "Certificados automáticos", "Emitidos ao concluir"], [FileText, "Biblioteca de conteúdo", "Downloads controlados"], [MessageSquareText, "Coaching 1:1", "Sessões agendadas"], [Bot, "Tutor IA", "Ajuda o aluno 24/7"], [TrendingUp, "Progresso e analytics", "Métricas por curso"]]}
        />
        <Modules />
        <HowItWorks />
        <Pricing />
        <Testimonials />
        <Faq />
        <CTA />
      </main>
      <Footer />

      {/* Floating WhatsApp */}
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-40 h-12 w-12 rounded-full bg-emerald-500 text-white grid place-items-center shadow-2xl shadow-emerald-500/30 hover:scale-110 transition" data-testid="landing-whatsapp">
        <MessageCircle className="h-5 w-5" />
      </a>
    </div>
  );
}
